import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Layout, LayoutContent, LayoutFooter, HStack } from "@astryxdesign/core/Layout";
import { FormLayout } from "@astryxdesign/core/FormLayout";
import { TextInput } from "@astryxdesign/core/TextInput";
import { Button } from "@astryxdesign/core/Button";
import { Banner } from "@astryxdesign/core/Banner";
import { useToast } from "@astryxdesign/core/Toast";
import { api } from "../../lib/api";
import type { User } from "../../lib/api";

interface ResetPasswordDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** The account being reset. Null just means the dialog has nothing to
   *  render yet — the parent controls isOpen independently. */
  target: User | null;
}

// Admin-only: sets another account's password with no proof of the old one.
// A distinct dialog (and a distinct backend endpoint) from the self-service
// change-my-own-password flow, which does require the current password —
// see UserUsecase.ResetPassword's doc comment for why that split exists.
export function ResetPasswordDialog({ isOpen, onOpenChange, target }: ResetPasswordDialogProps) {
  const queryClient = useQueryClient();
  const showToast = useToast();
  const [newPassword, setNewPassword] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setNewPassword("");
      setFieldError(null);
      setRequestError(null);
    }
  }, [isOpen]);

  const resetMutation = useMutation({
    mutationFn: () => {
      if (!target) return Promise.reject(new Error("no account selected"));
      return api.users.resetPassword(target.id, newPassword, target.version);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      showToast({ body: `Password reset for ${target?.username}` });
      onOpenChange(false);
    },
    onError: (err) => {
      setRequestError(err instanceof Error ? err.message : "Something went wrong.");
    },
  });

  // Shared by the footer button's onClick and the field's onEnter (no <form>
  // wraps this dialog — see CameraFormDialog's own comment on why) — pulled
  // out to a named function so the two can't quietly drift into validating
  // differently.
  const submit = () => {
    setRequestError(null);
    if (!newPassword.trim()) {
      setFieldError("New password is required.");
      return;
    }
    setFieldError(null);
    resetMutation.mutate();
  };

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} purpose="form" width={420}>
      <Layout
        header={
          <DialogHeader
            title={`Reset password${target ? ` for ${target.username}` : ""}`}
            subtitle="They'll need to sign in with this new password."
            onOpenChange={onOpenChange}
          />
        }
        content={
          <LayoutContent>
            <FormLayout>
              <TextInput
                label="New password"
                type="password"
                value={newPassword}
                onChange={setNewPassword}
                status={fieldError ? { type: "error", message: fieldError } : undefined}
                isRequired
                hasAutoFocus
                onEnter={() => {
                  if (!resetMutation.isPending) submit();
                }}
              />
              {requestError && <Banner status="error" title="Couldn't reset password" description={requestError} />}
            </FormLayout>
          </LayoutContent>
        }
        footer={
          <LayoutFooter>
            <HStack gap={2} hAlign="end">
              <Button
                label="Cancel"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                isDisabled={resetMutation.isPending}
              />
              <Button label="Reset password" variant="primary" isLoading={resetMutation.isPending} onClick={submit} />
            </HStack>
          </LayoutFooter>
        }
      />
    </Dialog>
  );
}
